import { useEffect, useState } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useDocument } from "../../hooks/useDocument";
import { useAuth } from "../../contexts/AuthContext";

const emptyForm = {
  // Hero
  heroTitleTh: "",
  heroTitleEn: "",
  heroSubtitle: "",
  heroButtonText: "",
  heroButtonUrl: "",

  // About
  aboutSubtitle: "",
  aboutHeading: "",
  aboutParagraph1: "",
  aboutParagraph2: "",
  aboutFeature1Title: "",
  aboutFeature1Desc: "",
  aboutFeature2Title: "",
  aboutFeature2Desc: "",

  // Contact
  contactAddress: "",
  contactEmail: "",
  contactPhone: "",
  facebookEmbedUrl: "",
  mapEmbedUrl: "",
};

export default function SettingsAdmin() {
  const { canEditSettings } = useAuth();
  const { data, loading } = useDocument("settings", "site");
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (data) setForm({ ...emptyForm, ...data });
  }, [data]);

  // Defense in depth — ถึง Firestore rules จะ block แต่ UI ก็ไม่ควรให้เข้า
  // หมายเหตุ: guard ต้องวาง *หลัง* hooks ทั้งหมดเพื่อไม่ให้ผิด Rules of Hooks
  if (!canEditSettings) {
    return (
      <div className="alert alert-warning">
        Site Settings เปลี่ยนได้เฉพาะ role Admin ขึ้นไป
      </div>
    );
  }

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await setDoc(doc(db, "settings", "site"), {
        ...form,
        updatedAt: serverTimestamp(),
      });
      setSavedAt(new Date());
    } catch (err) {
      alert(`บันทึกไม่สำเร็จ: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>กำลังโหลด...</div>;

  return (
    <div>
      <h2 className="mb-4">Site Settings</h2>

      <form onSubmit={handleSubmit}>
        {/* Hero */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">Hero (หน้าแรกส่วนบนสุด)</h5>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">หัวข้อภาษาไทย</label>
                <input className="form-control" value={form.heroTitleTh} onChange={update("heroTitleTh")} />
              </div>
              <div className="col-12">
                <label className="form-label">หัวข้อภาษาอังกฤษ</label>
                <input className="form-control" value={form.heroTitleEn} onChange={update("heroTitleEn")} />
              </div>
              <div className="col-12">
                <label className="form-label">คำบรรยายใต้หัวข้อ</label>
                <textarea className="form-control" rows={2} value={form.heroSubtitle} onChange={update("heroSubtitle")} />
              </div>
              <div className="col-md-5">
                <label className="form-label">ข้อความปุ่ม</label>
                <input className="form-control" value={form.heroButtonText} onChange={update("heroButtonText")} />
              </div>
              <div className="col-md-7">
                <label className="form-label">ลิงก์ปุ่ม</label>
                <input type="url" className="form-control" value={form.heroButtonUrl} onChange={update("heroButtonUrl")} />
              </div>
            </div>
          </div>
        </div>

        {/* About */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">About Us</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">คำอธิบายหัวข้อ (เล็ก)</label>
                <input className="form-control" value={form.aboutSubtitle} onChange={update("aboutSubtitle")} />
              </div>
              <div className="col-md-6">
                <label className="form-label">หัวข้อย่อย</label>
                <input className="form-control" value={form.aboutHeading} onChange={update("aboutHeading")} />
              </div>
              <div className="col-12">
                <label className="form-label">ย่อหน้าที่ 1</label>
                <textarea className="form-control" rows={3} value={form.aboutParagraph1} onChange={update("aboutParagraph1")} />
              </div>
              <div className="col-12">
                <label className="form-label">ย่อหน้าที่ 2</label>
                <textarea className="form-control" rows={3} value={form.aboutParagraph2} onChange={update("aboutParagraph2")} />
              </div>
              <div className="col-md-6">
                <label className="form-label">คุณสมบัติ 1 — หัวข้อ</label>
                <input className="form-control" value={form.aboutFeature1Title} onChange={update("aboutFeature1Title")} />
                <label className="form-label mt-2">คุณสมบัติ 1 — คำอธิบาย</label>
                <textarea className="form-control" rows={3} value={form.aboutFeature1Desc} onChange={update("aboutFeature1Desc")} />
              </div>
              <div className="col-md-6">
                <label className="form-label">คุณสมบัติ 2 — หัวข้อ</label>
                <input className="form-control" value={form.aboutFeature2Title} onChange={update("aboutFeature2Title")} />
                <label className="form-label mt-2">คุณสมบัติ 2 — คำอธิบาย</label>
                <textarea className="form-control" rows={3} value={form.aboutFeature2Desc} onChange={update("aboutFeature2Desc")} />
              </div>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">Contact</h5>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">ที่อยู่</label>
                <textarea className="form-control" rows={2} value={form.contactAddress} onChange={update("contactAddress")} />
              </div>
              <div className="col-md-6">
                <label className="form-label">อีเมล</label>
                <input type="email" className="form-control" value={form.contactEmail} onChange={update("contactEmail")} />
              </div>
              <div className="col-md-6">
                <label className="form-label">เบอร์โทร</label>
                <input className="form-control" value={form.contactPhone} onChange={update("contactPhone")} />
              </div>
              <div className="col-12">
                <label className="form-label">Facebook embed URL (src ของ iframe)</label>
                <input type="url" className="form-control" value={form.facebookEmbedUrl} onChange={update("facebookEmbedUrl")} />
                <div className="form-text">
                  ต้องขึ้นต้นด้วย <code>https://www.facebook.com/plugins/page.php?...</code>
                  — สร้างได้จาก{" "}
                  <a href="https://developers.facebook.com/docs/plugins/page-plugin" target="_blank" rel="noreferrer">
                    Facebook Page Plugin
                  </a>
                </div>
                {form.facebookEmbedUrl &&
                  !form.facebookEmbedUrl.includes("facebook.com/plugins/") && (
                    <div className="alert alert-warning py-2 small mt-2 mb-0">
                      ⚠️ URL นี้ไม่ใช่ embed URL — iframe จะไม่ load ตรวจสอบว่าใช้ลิงก์จาก Page Plugin
                    </div>
                  )}
              </div>
              <div className="col-12">
                <label className="form-label">Google Maps embed URL (src ของ iframe)</label>
                <input type="url" className="form-control" value={form.mapEmbedUrl} onChange={update("mapEmbedUrl")} />
                <div className="form-text">
                  ต้องขึ้นต้นด้วย <code>https://www.google.com/maps/embed?pb=...</code>
                  — จาก Google Maps: Share → <b>Embed a map</b> → COPY HTML → เอาเฉพาะ URL ใน <code>src=""</code>
                </div>
                {form.mapEmbedUrl &&
                  !form.mapEmbedUrl.includes("/maps/embed?") && (
                    <div className="alert alert-warning py-2 small mt-2 mb-0">
                      ⚠️ URL นี้ไม่ใช่ embed URL — iframe จะไม่ load (X-Frame-Options block) ต้องใช้ลิงก์จาก Share → Embed a map
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex gap-3 align-items-center">
          <button className="btn btn-primary btn-lg" disabled={saving}>
            {saving ? "กำลังบันทึก..." : "บันทึกทั้งหมด"}
          </button>
          {savedAt && (
            <span className="text-success small">
              ✓ บันทึกแล้วเมื่อ {savedAt.toLocaleTimeString("th-TH")}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
